import { useParams, Link, Navigate } from "react-router-dom";
import { useMemo } from "react";
import Layout from "@/components/site/Layout";
import Seo from "@/components/site/Seo";
import FaqBlock from "@/components/site/FaqBlock";
import ShareLocationButton from "@/components/site/ShareLocationButton";
import { getCity, CITY_SLUGS, CITIES } from "@/config/cities";
import { SERVICE_CONTENT, SERVICE_SLUGS } from "@/config/serviceContent";
import { PHONE_DISPLAY, PHONE_TEL } from "@/config/site";
import { citySchema, faqSchema, breadcrumbSchema } from "@/lib/schema";
import { Phone, MapPin, Building2, CheckCircle2, ArrowRight } from "lucide-react";
import NeedHelpBlock from "@/components/site/NeedHelpBlock";
import { CITY_LONG } from "@/config/longContent";

export default function CityPage({ overrideSlug }) {
  const params = useParams();
  const citySlug = overrideSlug || params.citySlug;
  const city = getCity(citySlug);

  // Memoize the "other cities" list so the filter+map only runs when the
  // current city changes (previously ran on every render). Computed before
  // the early-return null guard to satisfy the rules of hooks.
  const currentSlug = city?.slug || "";
  const otherCities = useMemo(
    () => CITY_SLUGS.filter((s) => s !== currentSlug).map((s) => CITIES[s]).filter(Boolean),
    [currentSlug]
  );

  if (!city) return <Navigate to="/" replace />;

  const url = typeof window !== "undefined" ? window.location.href : `/towing/${city.slug}`;
  const schemas = [
    citySchema({ city, url }),
    faqSchema(city.faqs),
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: `Towing in ${city.name}`, url: city.path },
    ]),
  ].filter(Boolean);

  // Stabilize the FAQ array so <FaqBlock> doesn't see a new reference each render.
  const faqItems = [
    ...city.faqs,
    ...(CITY_LONG[city.slug]?.extraFaqs || []),
  ];

  return (
    <Layout>
      <Seo
        title={city.metaTitle}
        description={city.metaDesc}
        canonical={url}
        jsonLd={schemas}
      />

      <section className="bg-[#0A0A0A] border-b border-yellow-400/30 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <nav className="flex items-center gap-2 text-xs font-display uppercase tracking-widest text-zinc-500 mb-4">
              <Link to="/" className="hover:text-yellow-400">Home</Link>
              <span>/</span>
              <Link to="/#areas" className="hover:text-yellow-400">Areas</Link>
              <span>/</span>
              <span className="text-yellow-400">{city.name}</span>
            </nav>
            <div className="inline-flex items-center gap-2 bg-yellow-400 text-slate-900 px-3 py-1 font-display font-black uppercase tracking-widest text-xs border-2 border-black">
              <MapPin className="h-3 w-3" strokeWidth={3} /> Serving {city.fullName}
            </div>
            <h1 className="mt-6 font-display font-black uppercase text-white text-4xl sm:text-6xl leading-[0.95] tracking-tight">
              {city.h1}
            </h1>
            <p className="mt-5 text-zinc-300 text-lg max-w-2xl">{city.hero}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid={`city-${city.slug}-call-hero`}
                className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-display font-black uppercase tracking-wider px-6 py-4 text-base border-2 border-black bm-hard-shadow min-h-[56px]"
              >
                <Phone className="h-5 w-5" strokeWidth={3} /> Call {PHONE_DISPLAY}
              </a>
              <ShareLocationButton
                testId={`city-${city.slug}-share`}
                variant="ghost"
                label={`I need a tow in ${city.name}`}
                className="text-base"
              />
            </div>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <div className="bg-black border-2 border-yellow-400 bm-hard-shadow-yellow p-5">
              <div className="font-display uppercase tracking-[0.2em] text-yellow-400 text-xs">Local coverage</div>
              <ul className="mt-3 space-y-1 text-sm text-zinc-300">
                {city.neighborhoods.slice(0, 6).map((n) => (
                  <li key={n} className="flex items-start gap-2">
                    <Building2 className="h-4 w-4 text-yellow-400 shrink-0 mt-0.5" />
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 font-display uppercase tracking-[0.2em] text-yellow-400 text-xs">Highways</div>
              <div className="mt-1 text-xs text-zinc-400">{city.highways.join(" · ")}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 border-b-2 border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <p className="text-zinc-300 text-lg leading-relaxed">{city.intro}</p>
            <p className="mt-4 text-zinc-400 text-base leading-relaxed">{city.localAngle}</p>

            <h2 className="mt-10 font-display font-black uppercase text-white text-3xl">Neighborhoods we cover in {city.name}</h2>
            <ul className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {city.neighborhoods.map((n) => (
                <li key={n} className="flex items-center gap-2 bg-[#0A0A0A] border-2 border-white/10 px-3 py-2 text-sm text-zinc-300">
                  <MapPin className="h-4 w-4 text-yellow-400 shrink-0" /> {n}
                </li>
              ))}
            </ul>

            {city.landmarks && (
              <>
                <h2 className="mt-10 font-display font-black uppercase text-white text-3xl">Local landmarks</h2>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {city.landmarks.map((l) => (
                    <li key={l} className="bg-[#0A0A0A] border-2 border-white/10 px-4 py-3 text-sm text-zinc-300">
                      {l}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {CITY_LONG[city.slug] && (
              <>
                <p className="mt-10 text-zinc-300 text-base leading-relaxed">
                  {CITY_LONG[city.slug].longIntro}
                </p>
                <h2 className="mt-8 font-display font-black uppercase text-white text-3xl">Corridors, parking &amp; access</h2>
                <p className="mt-3 text-zinc-300 text-base leading-relaxed">
                  {CITY_LONG[city.slug].corridorsAndParking}
                </p>
                <h2 className="mt-8 font-display font-black uppercase text-white text-3xl">What to know locally</h2>
                <p className="mt-3 text-zinc-300 text-base leading-relaxed">
                  {CITY_LONG[city.slug].localCaveats}
                </p>
                <p className="mt-4 text-zinc-300 text-base leading-relaxed">
                  {CITY_LONG[city.slug].landmarksBlock}
                </p>
              </>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="lux-glass-strong border-yellow-400/40 rounded-2xl p-6 sticky top-24">
              <div className="font-display uppercase tracking-[0.2em] text-yellow-400 text-xs">Services in {city.name}</div>
              <ul className="mt-3 space-y-2">
                {SERVICE_SLUGS.map((s) => {
                  const svc = SERVICE_CONTENT[s];
                  return (
                    <li key={s}>
                      <Link
                        to={svc.path}
                        data-testid={`city-${city.slug}-service-${s}`}
                        className="flex items-center justify-between gap-2 text-zinc-300 hover:text-yellow-400 border-b border-white/5 pb-2 group"
                      >
                        <span className="font-display uppercase tracking-widest text-xs">{svc.title}</span>
                        <ArrowRight className="h-3 w-3 text-yellow-400 opacity-0 group-hover:opacity-100" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid={`city-${city.slug}-call-side`}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-display font-black uppercase tracking-wider px-4 py-3 border-2 border-black"
              >
                <Phone className="h-5 w-5" strokeWidth={3} /> {PHONE_DISPLAY}
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 border-b-2 border-white/5 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="font-display uppercase tracking-[0.3em] text-yellow-400 text-sm">FAQ</div>
          <h2 className="mt-2 font-display font-black uppercase text-white text-3xl sm:text-4xl">
            Towing in {city.name} — Common Questions
          </h2>
          <div className="mt-6">
            <FaqBlock
              faqs={faqItems}
              testId={`city-${city.slug}-faq`}
            />
          </div>
        </div>
      </section>

      {/* Internal-linking help block */}
      <NeedHelpBlock testId={`city-${city.slug}-needhelp`} />

      {/* Other cities */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="font-display uppercase tracking-[0.3em] text-yellow-400 text-sm">Also serving</div>
          <h2 className="mt-2 font-display font-black uppercase text-white text-3xl">
            Other cities we cover
          </h2>
          <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {otherCities.map((c) => (
              <li key={c.slug}>
                <Link
                  to={c.path}
                  data-testid={`city-${city.slug}-related-${c.slug}`}
                  className="flex items-center gap-2 bg-[#0A0A0A] border-2 border-white/10 hover:border-yellow-400 px-3 py-3 text-zinc-300 hover:text-white transition-colors"
                >
                  <MapPin className="h-4 w-4 text-yellow-400" />
                  <span className="font-display uppercase tracking-widest text-xs">{c.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </Layout>
  );
}
